import Api from '@/config/api';
import axios from 'axios';
import router from '@/router';
import store from '../store';

const AUTH_EXPIRES_AT = 'auth_expires_at';
const AUTH_SESSION = 'auth_session';

let refreshPromise = null;

const isRefreshUrl = (url) => url && url.includes(Api.endpoints.REFRESH);
const isLogoutUrl = (url) => url && url.includes(Api.endpoints.OAUTH_LOGOUT);

export default function (Vue) {
  Vue.auth = {
    setToken(data) {
      const expiresIn = data && (data.expires_in !== undefined ? data.expires_in : (data.data && data.data.expires_in));
      if (expiresIn != null) {
        localStorage.setItem(AUTH_EXPIRES_AT, String(Date.now() + expiresIn * 1000));
      }
      localStorage.setItem(AUTH_SESSION, '1');
    },

    async getToken() {
      const expiresAt = localStorage.getItem(AUTH_EXPIRES_AT);
      const now = Date.now();
      const expired = !expiresAt || now > parseFloat(expiresAt, 10);
      if (expired) {
        await this.refreshToken();
      }
    },

    async refreshToken() {
      if (refreshPromise) return refreshPromise;
      refreshPromise = axios({
        method: 'post',
        url: Api.url(Api.endpoints.REFRESH),
        withCredentials: true,
      })
        .then((response) => {
          const data = response.data && response.data.data ? response.data.data : response.data;
          const expiresIn = data && data.expires_in;
          if (expiresIn != null) {
            localStorage.setItem(AUTH_EXPIRES_AT, String(Date.now() + expiresIn * 1000));
          }
          localStorage.setItem(AUTH_SESSION, '1');
          return response;
        })
        .catch((err) => {
          this.logout();
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
      return refreshPromise;
    },

    destroyToken() {
      localStorage.removeItem(AUTH_EXPIRES_AT);
      localStorage.removeItem(AUTH_SESSION);
      localStorage.removeItem('access_token');
      localStorage.removeItem('expiration');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('hasViewedScoutPopup');
    },

    isAuthenticated() {
      return !!localStorage.getItem(AUTH_SESSION);
    },

    async logout() {
      try {
        await axios({
          method: 'post',
          url: Api.url(Api.endpoints.OAUTH_LOGOUT),
          withCredentials: true,
        });
      } catch (e) {
        // ignore
      }
      this.destroyToken();
      store.commit('clearAll');
      router.push({ name: 'login' });
    },
  };

  Object.defineProperties(Vue.prototype, {
    $auth: {
      get() {
        return Vue.auth;
      },
    },
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const url = originalRequest && originalRequest.url;
      if (error.response && error.response.status === 401) {
        if (originalRequest && originalRequest.headers && originalRequest.headers['X-Skip-Auth-Interceptor']) {
          return Promise.reject(error);
        }
        if (isRefreshUrl(url) || isLogoutUrl(url)) {
          Vue.auth.destroyToken();
          store.commit('clearAll');
          router.push({ name: 'login' });
          return Promise.reject(error);
        }
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          try {
            await Vue.auth.refreshToken();
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }
      return Promise.reject(error);
    },
  );
}
